/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-04-12 10:58:25
 * @LastEditors: Jack Deng
 * @LastEditTime: 2023-03-13 16:22:09
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/Components/ProductList/index.tsx
 */
import React, { ReactNode, useState } from 'react';

import { BulkEditor, Filter } from '@/src/components';
import { SizeFittingTable, Tag as _Tag } from '@/src/components/Antd';
import DropDown from '@/src/components/Antd/DropDown/DropDown';
import NoPermission from '@/src/components/NoPermission';
import Sort from '@/src/constrains/Query/Sort';
import { Icon } from '@/src/tools/icon';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Tabs } from 'antd';
import Pagination from 'src/components/StyledPagination/GlobalPagination';
import TagGroup from 'src/components/TagGroup/TagGroup';
import { useI18n } from 'src/hooks/useRouterChange';
import { defaultPageSize } from 'src/tools/constant';
import { getI18nFromGeneral, i18nGet } from 'src/tools/tools';
import { typeDict } from 'src/types/common/salesChannelProduct';
import { filterConfig, sortConfig } from './config';
import styles from './style.module.less';
import { isNumber } from 'lodash';
import { CreateButtonWithLanguageChangeWarning } from '@/src/constrains';

const Tag = (props: any) => {
  const { children, ...rest } = props;
  return (
    <_Tag {...rest} className={styles.tag}>
      {children}
    </_Tag>
  );
};

type UserPermissions = {
  canViewListing: boolean;
  canViewDetails: boolean;
  canCreate: boolean;
  canDelete: boolean;
};

type Props = {
  salesChannelUuid: string;
  total: number;
  products: Array<any>;
  tagList: Array<any>;
  currency: string;
  addButtonOverlay: ReactNode;
  userPermissions: UserPermissions;
  onSearch: (obj: Record<string, any>) => void;
  onDelete: (productIds: Array<string>) => void;
  onNavigateToProductDetail: (record: any) => void;
  onChangeVisiblity: (visible: boolean, uuid: string) => void;
  onClickCreate: () => void;
  decimalPosition?: number;
};

function ProductList({
  salesChannelUuid,
  total,
  products,
  tagList,
  currency,
  addButtonOverlay,
  userPermissions,
  onSearch,
  onDelete,
  onNavigateToProductDetail,
  onChangeVisiblity,
  onClickCreate,
  decimalPosition,
}: Props) {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [productTypes, setProductTypes] = useState('');
  const [visibility, setVisiblity] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedProducts, setSelectProducts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({
    name: getI18n('general.productname(A-Z)'),
    key: 'productName 1',
  });

  const handleValueOnChange = (value) => setSearchValue(value);

  const handleSearch = () => {
    if (onSearch) {
      onSearch(getParams(true));
    }
  };

  const handleClickFilter = (item, list) => {
    const params = getParams(true);
    if (item.name === getI18nFromGeneral('general.status.type')) {
      const type =
        Array.isArray(list) && list.length !== 0 ? list[0].key : null;
      setProductTypes(type);
      if (onSearch) {
        onSearch({
          ...params,
          body: {
            ...params.body,
            productTypes: type ? [type] : null,
          },
        });
      }
    }
    if (item.name === getI18nFromGeneral('general.status.visibility')) {
      const type =
        Array.isArray(list) && list.length !== 0 ? list[0].key : null;
      if (type === 'visible') {
        setVisiblity('visible');
        if (onSearch) {
          onSearch({
            ...params,
            body: {
              ...params.body,
              visible: true,
            },
          });
        }
      } else {
        if (onSearch) {
          setVisiblity('invisible');
          onSearch({
            ...params,
            body: {
              ...params.body,
              visible: false,
            },
          });
        }
      }
    }
    if (item.name === getI18nFromGeneral('general.hOCTitle.tags')) {
      const result = [
        ...tags,
        ...list.map((item) => ({
          ...item,
          uuid: item.key,
        })),
      ];
      setTags(result);
      if (onSearch) {
        onSearch({
          ...params,
          body: {
            ...params.body,
            tags: result.map((t) => ({ uuid: t.key, name: t.name })),
          },
        });
      }
    }
  };

  const handleDeleteProducts = (selectedRowKeys) => {
    onDelete(selectedRowKeys);
    setSelectProducts([]);
  };

  const handleClickSort = (list) => {
    const params = getParams(true);
    setSort(list);
    const [field, ascent] = list.key.split(' ');
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          sortBy: {
            field,
            ascent: ascent === '1',
          },
        },
      });
    }
  };

  const handleSelectChange = (selectedRowKeys) => {
    setSelectProducts(selectedRowKeys.filter((v) => v));
  };

  const handleDeleteProductTypes = () => {
    const params = getParams(true);
    setProductTypes('');
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          productTypes: null,
        },
      });
    }
  };

  const handleDeleteVisibility = () => {
    const params = getParams(true);
    setVisiblity('');
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          visible: null,
        },
      });
    }
  };

  const handleDeleteTags = (tag) => {
    const params = getParams(true);
    const result = tags.filter((t) => t.key !== tag.key);
    setTags(result);
    if (onSearch) {
      onSearch({
        ...params,
        body: {
          ...params.body,
          tags: result.map((t) => ({ uuid: t.key, name: t.name })),
        },
      });
    }
  };

  const handlePageChange = (page) => {
    const params = getParams();
    setCurrent(page);
    if (onSearch) {
      onSearch({
        ...params,
        startIndex: (page - 1) * pageSize,
      });
    }
  };

  const handlePageSizeChange = (e) => {
    const params = getParams();
    const size = e.value;
    setPageSize(size);
    if (onSearch) {
      onSearch({
        ...params,
        startIndex: (current - 1) * size,
        maxCount: size,
      });
    }
  };

  const getParams = (resetCurrent = false) => {
    const [field, ascent] = sort.key.split(' ');
    const req = {
      productTypes: productTypes ? [productTypes] : null,
      searchText: searchValue ? [searchValue] : null,
      tags,
      visible:
        visibility === 'visible'
          ? true
          : visibility === 'invisible'
          ? false
          : null,
      sortBy: {
        field,
        ascent: ascent === '1',
      },
    };
    if (resetCurrent) {
      setCurrent(1);
    }
    return {
      body: req,
      startIndex: (resetCurrent ? 0 : current - 1) * pageSize,
      maxCount: pageSize,
    };
  };

  const columns = [
    {
      title: getI18n('general.products'),
      key: 'products',
      default: true,
      minWidth: 295,
      childGenerate: (_, record, index) => {
        return (
          <div className={styles.products}>
            {record.thumbnailUrl && (
              <div
                className={styles.img}
                style={{ backgroundImage: `url('${record.thumbnailUrl}')` }}
              />
            )}
            <div className={styles.productName}>
              <div className={styles.name}>{record.productName}</div>
              {record.hasError && (
                <ExclamationCircleFilled className={styles.draftNameErr} />
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: getI18n('general.type'),
      dataIndex: 'productType',
      key: 'productType',
      default: true,
      width: 150,
      childGenerate: (type, record, index) => {
        // const getDisplay = () => {
        //   if (type.startsWith('Single')) {
        //     return 'Single';
        //   }
        //   if (type.startsWith('Multiple')) {
        //     return 'Multiple';
        //   }
        //   return '-';
        // };
        return typeDict[type];
      },
    },
    // {
    //   title: 'Available Qty',
    //   key: 'product',
    //   dataIndex: 'product',
    //   default: true,
    //   width: 111,
    //   childGenerate: () => {
    //     return <div />;
    //   },
    // },
    {
      title: `${getI18n('general.listPrice')} (${currency})`,
      key: 'listPrice',
      dataIndex: 'listPrice',
      default: true,
      width: 120,
      childGenerate: (text, record) => {
        //!record.child ? text || '' : text;
        if (record.child) {
          if (isNumber(text) && isNumber(decimalPosition)) {
            return text.toFixed(decimalPosition);
          } else {
            return text || '-';
          }
        } else {
          if (isNumber(text) && isNumber(decimalPosition)) {
            return text.toFixed(decimalPosition);
          } else {
            return text || '-';
          }
        }
      },
    },
    {
      title: getI18n('general.tags'),
      dataIndex: 'tags',
      default: true,
      optional: true,
      align: 'center',
      minWidth: 192,
      tags: true,
      tagsGenerator: (
        characterLength,
        width,
        marginPaddingWidth,
        containPaddingWidth,
        tags,
        record,
      ) => {
        return (
          <TagGroup
            characterLength={characterLength}
            containerWidth={width}
            marginPaddingWidth={marginPaddingWidth}
            containPaddingWidth={containPaddingWidth}
            tags={tags}
          />
        );
      },
    },
    {
      title: getI18n('general.visibility'),
      dataIndex: 'visible',
      key: 'visible',
      width: 158,
      default: true,
      childGenerate: (text, record) => {
        return record.child ? null : text ? (
          <span style={{ color: '#38C568' }}>
            {userPermissions.canCreate && (
              <Icon
                type={'iconvisible'}
                className={styles.visible}
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeVisiblity(false, record.uuid);
                }}
              />
            )}
            {getI18n('general.visible')}
          </span>
        ) : (
          <span style={{ color: '#5C6269' }}>
            {userPermissions.canCreate && (
              <Icon
                type={'iconinvisible'}
                className={styles.visible}
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeVisiblity(true, record.uuid);
                }}
              />
            )}
            {getI18n('general.invisible')}
          </span>
        );
      },
    },
  ];

  const renderCell = (checked, record, index, originNode) => {
    //console.log('originNode', originNode);
    if (record.child) {
      return null;
    } else {
      return originNode;
    }
  };

  const visibilityI18nMapping = {
    visible: 'general.visible',
    invisible: 'general.invisible',
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <span className={styles.titleLeft}>{getI18n('general.products')}</span>
        {addButtonOverlay ? (
          <DropDown
            className={styles.dropDown}
            overlay={addButtonOverlay}
            trigger={['click']}
            disabled={!userPermissions.canCreate}>
            <CreateButtonWithLanguageChangeWarning
              salesChannelUuid={salesChannelUuid}
              title={getI18n('general.addProducts')}
              content={getI18n(
                'salesChannelProductList.index.createProductsContent',
              )}>
              {getI18n('general.addProducts')}
            </CreateButtonWithLanguageChangeWarning>
          </DropDown>
        ) : (
          <CreateButtonWithLanguageChangeWarning
            salesChannelUuid={salesChannelUuid}
            title={getI18n('salesChannelProductList.index.createProducts')}
            onClick={onClickCreate}
            content={getI18n(
              'salesChannelProductList.index.createProductsContent',
            )}
            disabled={!userPermissions.canCreate}>
            {getI18n('salesChannelProductList.index.createProducts')}
          </CreateButtonWithLanguageChangeWarning>
        )}
      </div>
      {userPermissions.canViewListing ? (
        <>
          <div className={styles.tabs}>
            <Tabs tabBarStyle={{ height: '56px' }}>
              <Tabs.TabPane
                tab={
                  <div className={styles.tabPane}>{getI18n('general.all')}</div>
                }
                key='all'></Tabs.TabPane>
            </Tabs>
          </div>
          <div className={styles.action}>
            <Filter
              searchValue={searchValue}
              searchOnchange={handleValueOnChange}
              pressEnter={handleSearch}
              onSearch={handleSearch}
              overlayClick={handleClickFilter}
              filterList={filterConfig(tagList)}
            />
            <div className={styles.actionRight}>
              <Sort
                style={{ width: '180px' }}
                sortList={sortConfig({ getI18n })}
                name={sort.name}
                sortClick={handleClickSort}
                sortKey={sort.key}
              />
            </div>
          </div>
          <div className={styles.filters}>
            {productTypes && (
              <Tag closable onClose={handleDeleteProductTypes}>
                {typeDict[productTypes]}
              </Tag>
            )}
            {visibility && (
              <Tag closable onClose={handleDeleteVisibility}>
                {getI18n(visibilityI18nMapping[visibility])}
              </Tag>
            )}
            {tags.length !== 0 &&
              tags.map((tag) => (
                <Tag
                  key={tag.key}
                  closable
                  onClose={handleDeleteTags.bind(this, tag)}>
                  {tag.name}
                </Tag>
              ))}
          </div>
          <div className={styles.editor}>
            <BulkEditor
              buttonMenu={[
                {
                  key: 'delete',
                  value: getI18n('general.delete'),
                  function: handleDeleteProducts,
                  disabled: !userPermissions.canDelete,
                },
              ]}
              selectedRowKeys={selectedProducts}
              hideSelectAll
            />
          </div>
          <div className={styles.table}>
            <SizeFittingTable
              rowKey='uuid'
              pagination={false}
              id='salesChannelProductList'
              dataSource={products}
              columns={columns}
              rowClassName={styles.row}
              onRow={(record) => ({
                onClick:
                  userPermissions.canViewDetails &&
                  onNavigateToProductDetail.bind(this, record),
              })}
              rowSelection={{
                onChange: handleSelectChange,
                renderCell,
              }}
            />
            {userPermissions.canViewListing && (
              <Pagination
                current={current}
                total={total}
                pageSize={pageSize}
                pageOnChange={handlePageChange}
                pageSizeChange={handlePageSizeChange}
                paginationProps={{ defaultPageSize: pageSize }}
              />
            )}
          </div>
        </>
      ) : (
        <NoPermission />
      )}
    </div>
  );
}

export default ProductList;
